import { Badge } from "@frontend/components/ui/badge"
import { Button } from "@frontend/components/ui/button"
import { Input } from "@frontend/components/ui/input"
import { Label } from "@frontend/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@frontend/components/ui/select"
import { Switch } from "@frontend/components/ui/switch"
import {
  ModuleTemplateEditorDialog,
  type ModuleTemplateEditorSubmitPayload
} from "@frontend/pages/module-templates/components/module-template-editor-dialog"
import { Plus, Trash2 } from "lucide-react"

import { formatDependencyCardinality, formatDependencyMode, formatModuleType } from "../lib/utils"
import type { ModuleTemplate, ProjectModuleItem } from "../types"
import { ModuleCompletionRequirements } from "./module-config/module-completion-requirements"
import { ModuleEditRegions } from "./module-config/module-edit-regions"
import { SchemaSummary } from "./schema-summary"

interface ProjectFormModulesTabProps {
  modulesLoading: boolean
  modules: ProjectModuleItem[]
  selectedModuleId: string
  setSelectedModuleId: (value: string) => void
  selectedModule: ProjectModuleItem | undefined
  selectedTemplate: ModuleTemplate | undefined
  moduleTypeBadgeClass: (type: string) => string
  addableTemplates: ModuleTemplate[]
  newTemplateId: string
  setNewTemplateId: (value: string) => void
  onAddModule: () => void
  isAddModulePending: boolean
  createTemplateOpen: boolean
  setCreateTemplateOpen: (value: boolean) => void
  editTemplateOpen: boolean
  setEditTemplateOpen: (value: boolean) => void
  onCreateTemplate: (payload: ModuleTemplateEditorSubmitPayload) => void
  isCreateTemplatePending: boolean
  createTemplateError?: string
  onEditTemplate: (payload: ModuleTemplateEditorSubmitPayload) => void
  isEditTemplatePending: boolean
  editTemplateError?: string
  dependsOnModuleId: string
  setDependsOnModuleId: (value: string) => void
  availableDependencyTargets: ProjectModuleItem[]
  dependencyMode: "completion_only" | "instance_linked"
  setDependencyMode: (value: "completion_only" | "instance_linked") => void
  dependencyCardinality: "one_to_one" | "one_to_many_min1"
  setDependencyCardinality: (value: "one_to_one" | "one_to_many_min1") => void
  dependencyLabelFilter: string
  setDependencyLabelFilter: (value: string) => void
  dependencyAutoCreate: boolean
  setDependencyAutoCreate: (value: boolean) => void
  dependencyCascadeDelete: boolean
  setDependencyCascadeDelete: (value: boolean) => void
  onAddDependencyRule: () => void
  isAddDependencyPending: boolean
  onDeleteDependency: (depId: string) => void
  isDeleteDependencyPending: boolean
  onDeleteModule: () => void
  isDeleteModulePending: boolean
  onUpdateModuleConfig: (moduleId: string, config: Record<string, unknown> | null) => void
  isUpdateModuleConfigPending: boolean
}

export function ProjectFormModulesTab({
  modulesLoading,
  modules,
  selectedModuleId,
  setSelectedModuleId,
  selectedModule,
  selectedTemplate,
  moduleTypeBadgeClass,
  addableTemplates,
  newTemplateId,
  setNewTemplateId,
  onAddModule,
  isAddModulePending,
  createTemplateOpen,
  setCreateTemplateOpen,
  editTemplateOpen,
  setEditTemplateOpen,
  onCreateTemplate,
  isCreateTemplatePending,
  createTemplateError,
  onEditTemplate,
  isEditTemplatePending,
  editTemplateError,
  dependsOnModuleId,
  setDependsOnModuleId,
  availableDependencyTargets,
  dependencyMode,
  setDependencyMode,
  dependencyCardinality,
  setDependencyCardinality,
  dependencyLabelFilter,
  setDependencyLabelFilter,
  dependencyAutoCreate,
  setDependencyAutoCreate,
  dependencyCascadeDelete,
  setDependencyCascadeDelete,
  onAddDependencyRule,
  isAddDependencyPending,
  onDeleteDependency,
  isDeleteDependencyPending,
  onDeleteModule,
  isDeleteModulePending,
  onUpdateModuleConfig,
  isUpdateModuleConfigPending
}: ProjectFormModulesTabProps) {
  return (
    <>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[360px_1fr]">
        <div className="rounded-xl border bg-card/70 p-4">
          <div className="mb-4 space-y-2">
            <h3 className="text-lg font-semibold">Active Modules</h3>
            <div className="space-y-2 min-w-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCreateTemplateOpen(true)}
                className="w-full justify-center"
              >
                <Plus className="h-4 w-4 mr-1" /> New Template
              </Button>
              <div className="grid grid-cols-[1fr_auto] gap-2 min-w-0">
                <Select value={newTemplateId} onValueChange={(value) => setNewTemplateId(value ?? "")}>
                  <SelectTrigger className="w-full min-w-0">
                    <SelectValue placeholder="Choose template">
                      {addableTemplates.find((t) => t.id === newTemplateId)?.name}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
                    {addableTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="shrink-0"
                  onClick={onAddModule}
                  disabled={!newTemplateId || isAddModulePending}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
            </div>
          </div>

          {modulesLoading ? (
            <p className="text-sm text-muted-foreground">Loading modules...</p>
          ) : modules.length === 0 ? (
            <p className="text-sm text-muted-foreground">No modules active yet.</p>
          ) : (
            <div className="space-y-2">
              {modules.map((module) => (
                <button
                  type="button"
                  key={module.id}
                  onClick={() => setSelectedModuleId(module.id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                    selectedModule?.id === module.id
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/40"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{module.template_name}</span>
                    <Badge className={moduleTypeBadgeClass(module.template_type)}>
                      {formatModuleType(module.template_type)}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border bg-card/70 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Module Details</h3>
            <Button size="sm" variant="outline" disabled={!selectedModule} onClick={() => setEditTemplateOpen(true)}>
              Edit Template
            </Button>
          </div>
          {!selectedModule ? (
            <p className="text-sm text-muted-foreground mt-2">Select a module to view details.</p>
          ) : (
            <>
              <div className="mt-3 rounded-lg border bg-background/60 p-4 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <p className="text-sm font-medium">{selectedModule.template_name}</p>
                  <Badge className={moduleTypeBadgeClass(selectedModule.template_type)}>
                    {formatModuleType(selectedModule.template_type)}
                  </Badge>
                  <Badge variant="secondary">v{selectedTemplate?.version ?? 1}</Badge>
                  {selectedTemplate?.is_builtin ? (
                    <Badge variant="outline">Built-in</Badge>
                  ) : (
                    <Badge variant="outline">Custom</Badge>
                  )}
                  {selectedTemplate ? (
                    <Badge variant="outline">
                      Used in {selectedTemplate.usage_count} project
                      {selectedTemplate.usage_count === 1 ? "" : "s"}
                    </Badge>
                  ) : null}
                </div>
                <p className="text-sm text-muted-foreground mb-2">What this module collects</p>
                <SchemaSummary type={selectedModule.template_type} schema={selectedModule.template_schema} />
              </div>
              <div className="mt-4">
                <p className="text-sm font-semibold">Module order and linking</p>
                <div className="mt-2 rounded-lg border p-3 space-y-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs">This module starts after</Label>
                      <Select value={dependsOnModuleId} onValueChange={(v) => setDependsOnModuleId(v ?? "")}>
                        <SelectTrigger className="w-full min-w-0">
                          <SelectValue placeholder="Select module">
                            {availableDependencyTargets.find((m) => m.id === dependsOnModuleId)?.template_name}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
                          {availableDependencyTargets.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.template_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">How this rule works</Label>
                      <Select
                        value={dependencyMode}
                        onValueChange={(v) => setDependencyMode(v as "completion_only" | "instance_linked")}
                      >
                        <SelectTrigger className="w-full min-w-0">
                          <SelectValue>{formatDependencyMode(dependencyMode)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
                          <SelectItem value="completion_only">After completion</SelectItem>
                          <SelectItem value="instance_linked">Match related findings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">How many related items</Label>
                      <Select
                        value={dependencyCardinality}
                        onValueChange={(v) => setDependencyCardinality(v as "one_to_one" | "one_to_many_min1")}
                        disabled={dependencyMode !== "instance_linked"}
                      >
                        <SelectTrigger className="w-full min-w-0">
                          <SelectValue>{formatDependencyCardinality(dependencyCardinality)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
                          <SelectItem value="one_to_one">One related item</SelectItem>
                          <SelectItem value="one_to_many_min1">One or more related items</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Only for these labels (comma-separated)</Label>
                      <Input
                        placeholder="lesion"
                        value={dependencyLabelFilter}
                        onChange={(e) => setDependencyLabelFilter(e.target.value)}
                        disabled={dependencyMode !== "instance_linked"}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={dependencyAutoCreate}
                        onCheckedChange={(checked) => setDependencyAutoCreate(Boolean(checked))}
                        disabled={dependencyMode !== "instance_linked"}
                      />
                      <Label className="text-xs">Automatically create related items</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={dependencyCascadeDelete}
                        onCheckedChange={(checked) => setDependencyCascadeDelete(Boolean(checked))}
                        disabled={dependencyMode !== "instance_linked"}
                      />
                      <Label className="text-xs">Remove related items when source is removed</Label>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={onAddDependencyRule}
                    disabled={!(selectedModule && dependsOnModuleId) || isAddDependencyPending}
                  >
                    {isAddDependencyPending ? "Adding..." : "Add Rule"}
                  </Button>
                </div>
                {selectedModule.dependencies.length === 0 ? (
                  <p className="text-sm text-muted-foreground mt-1">No rules added yet.</p>
                ) : (
                  <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
                    {selectedModule.dependencies.map((dep) => {
                      const depModule = modules.find((m) => m.id === dep.depends_on_module_id)
                      return (
                        <li key={dep.id} className="rounded border p-2 flex items-start justify-between gap-2">
                          <div>
                            <div className="font-medium text-foreground">
                              Starts after: {depModule?.template_name ?? dep.depends_on_module_id}
                            </div>
                            <div className="text-xs">
                              Rule: {formatDependencyMode(dep.dependency_mode)} | Related items:{" "}
                              {formatDependencyCardinality(dep.cardinality)}
                            </div>
                            {dep.source_label_names.length > 0 && (
                              <div className="text-xs">Applies to labels: {dep.source_label_names.join(", ")}</div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Remove dependency"
                            onClick={() => onDeleteDependency(dep.id)}
                            disabled={isDeleteDependencyPending}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
              {selectedModule && (
                <ModuleCompletionRequirements
                  module={selectedModule}
                  onSave={(payload) => onUpdateModuleConfig(selectedModule.id, payload.config)}
                  isSaving={isUpdateModuleConfigPending}
                />
              )}
              {selectedModule && (
                <ModuleEditRegions
                  module={selectedModule}
                  onSave={(payload) => onUpdateModuleConfig(selectedModule.id, payload.config)}
                  isSaving={isUpdateModuleConfigPending}
                />
              )}
              <div className="mt-5">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  onClick={onDeleteModule}
                  disabled={isDeleteModulePending}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove Module
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <ModuleTemplateEditorDialog
        open={createTemplateOpen}
        mode="create"
        isPending={isCreateTemplatePending}
        errorMessage={createTemplateError}
        onOpenChange={setCreateTemplateOpen}
        onSubmit={onCreateTemplate}
      />
      <ModuleTemplateEditorDialog
        open={editTemplateOpen}
        mode="edit"
        initialValue={
          selectedModule
            ? {
                name: selectedTemplate?.name ?? selectedModule.template_name,
                type: selectedModule.template_type,
                schema: selectedTemplate?.schema ?? selectedModule.template_schema,
                version: selectedTemplate?.version ?? 1
              }
            : undefined
        }
        isPending={isEditTemplatePending}
        errorMessage={editTemplateError}
        onOpenChange={setEditTemplateOpen}
        onSubmit={onEditTemplate}
      />
    </>
  )
}
